import React from 'react';
import * as Card from '../../v2BaseComponents/Card';
import { getIntl } from '../../../utils/intl';
import { APITypes } from '../../../primary-record-types';
import getDateString from '../../../utils/getDateString';
import getName from '../../../utils/getName';
import { View } from 'react-native';
import Stack from '../../v2BaseComponents/Stack';
import MedicalProfileFileIconDisplay from '../MedicalProfileFileIconDisplay';
import useMedicalProfileFiles from '../../../hooks/api/useMedicalProfileFiles';

const intl = getIntl('FileCard', {
    uploaded: 'Uploaded at {date}',
    uploadedAtAndBy: 'Uploaded at {date} by {name}',
    fileProcessing: 'File is processing...',
    processingFailed: 'File processing failed',
    transcribed: 'Transcribed',
});

export type FileCardOwnProps = {
    onPress?: () => void;
    file: APITypes.medicalProfileFilesService.MedicalProfileFileListItem;
    isDisabled?: boolean;
};

export type FileCardProps = FileCardOwnProps;

const imageSize = 60;

const FileCard: React.FC<FileCardProps> = (props) => {
    const getMutedText = () => {
        if (props.file.uploadUser) {
            return intl('uploadedAtAndBy', {
                date: getDateString(props.file.dateAdded, 'date-time'),
                name: getName(props.file.uploadUser),
            });
        } else {
            return intl('uploaded', { date: getDateString(props.file.dateAdded, 'date-time') });
        }
    };

    return (
        <Card.Card onPress={props.onPress} isDisabled={props.isDisabled} testID="file-card">
            <Card.CardLeftContent
                leftContent={
                    <View
                        style={{
                            width: imageSize,
                            height: imageSize,
                        }}
                        id="file-card-image-container"
                    >
                        <MedicalProfileFileIconDisplay file={props.file} width={imageSize} height={imageSize} />
                    </View>
                }
            >
                <Stack vertical fullWidth justify="center" align="flex-start" space={1}>
                    <Card.CardListHeader>{props.file.fileName}</Card.CardListHeader>
                    {/*props.file.summary ? <Card.CardBody2>{props.file.summary}</Card.CardBody2> : null*/}
                    <Card.CardBody2>{getMutedText()}</Card.CardBody2>
                    {props.file.processingStatus === 'processing' ? (
                        <Card.CardCaption>{intl('fileProcessing')}</Card.CardCaption>
                    ) : null}
                    {props.file.processingStatus === 'failed' ? (
                        <Card.CardCaptionError>{intl('processingFailed')}</Card.CardCaptionError>
                    ) : null}
                    {props.file.processingStatus === 'processed' && props.file.processingType === 'audio' ? (
                        <Card.CardChip title={intl('transcribed')} />
                    ) : null}
                </Stack>
            </Card.CardLeftContent>
        </Card.Card>
    );
};

export default React.memo(FileCard);

export type UnloadedFileCardProps = {
    medicalProfileFileId: string;
    medicalProfileId: string;
    onPress?: () => void;
    skeletonFallback?: boolean;
};

export const UnloadedFileCard: React.FC<UnloadedFileCardProps> = ({
    medicalProfileFileId,
    medicalProfileId,
    onPress,
    skeletonFallback,
}) => {
    const { isLoadingFile, file } = useMedicalProfileFiles({
        medicalProfileId,
        medicalProfileFileId,
    });

    if (!file || isLoadingFile) return skeletonFallback ? <Card.CardSkeleton /> : null;

    return <FileCard onPress={onPress} file={file} />;
};
